import { Form, Input, ModalProps } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import TextArea from "antd/es/input/TextArea";
import ModalWithBorders from "components/ModalWithBorders";
import { genericError } from "fixtures/globalConstants";
import { useEffect, useState } from "react";

type EditMemoryModalValues = {
  title: string;
  description: string;
};

type EditMemoryModalProps = ModalProps & {
  initialValues?: EditMemoryModalValues;
  onSubmit: (values: EditMemoryModalValues) => Promise<void> | void;
  onClose: () => void;
};

// Rename component function
const EditMemoryModal = ({
  initialValues,
  onSubmit,
  onClose,
  ...modalProps // Pass remaining ModalProps (like title, open)
}: EditMemoryModalProps) => {
  const [form] = useForm<EditMemoryModalValues>();
  const title = useWatch("title", form);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Reset fields when initialValues change
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const placeholders = {
    title: "Title of the memory",
    description:
      "Describe whatever you want us to remember for future posts. Examples include a couple sentences about the services you offer or an essay about the history of your company.",
  };

  const handleSave = async (formData: EditMemoryModalValues) => {
    setIsLoading(true);
    try {
      await onSubmit(formData);
      // No specific tracking event here, could be added if needed
      onClose(); // Close modal on successful submission
    } catch (error) {
      genericError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWithBorders
      okButtonProps={{ loading: isLoading }}
      okText="Save"
      onOk={form.submit}
      onCancel={onClose}
      {...modalProps}
    >
      <Form
        onFinish={handleSave}
        id="edit-memory-modal-form" // Update ID
        layout="vertical"
        form={form}
        requiredMark={false}
      >
        {/* Title Field */}
        <Form.Item
          rules={[
            {
              required: true,
              max: 50,
              message: "Title is required (max 50 chars)",
            },
          ]}
          label="Title"
          name="title"
        >
          <Input
            placeholder={placeholders.title}
            suffix={
              <span className="text-antd-colorTextSecondary">
                {title?.length ?? 0} / 50
              </span>
            }
          />
        </Form.Item>

        {/* No Category Field for Memories */}

        {/* Description Field */}
        <Form.Item
          rules={[{ required: true, message: "Description is required" }]}
          label="Description"
          name="description"
        >
          <TextArea placeholder={placeholders.description} rows={3} />
        </Form.Item>
      </Form>
    </ModalWithBorders>
  );
};

// Update export
export default EditMemoryModal;
